import type { ChangeEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import {
  hasDuplicateMapping,
  type InventoryExcelDuplicateGroup,
  type InventoryExcelParseResult,
  type InventoryExcelRowError,
  type InventoryFieldMapping,
  type InventoryMappingField,
} from '@/utils/inventoryExcel';
import type { BulkUpsertMode, BulkUpsertResult } from '@/supabase/inventory/types/inventory';

// ExcelImportModalContainer의 스텝별 dumb 프레젠테이션 컴포넌트(PR5 T3). 상태/핸들러는 전부
// 컨테이너가 소유하고 이 파일은 props만 받아 렌더한다 — Dialog 래퍼·헤더 문구·푸터 버튼은
// 컨테이너가 스텝 전환 로직과 함께 중앙에서 관리(여기서 중복하지 않음).

const FIELD_ORDER: InventoryMappingField[] = ['name', 'stock', 'price', 'threshold'];
const FIELD_LABELS: Record<InventoryMappingField, string> = {
  name: '상품명',
  stock: '현재고',
  price: '판매가',
  threshold: '부족 기준',
};
const FIELD_DEFAULT_HINT: Record<InventoryMappingField, string | null> = {
  name: null,
  stock: '기본값 0',
  price: '기본값 0',
  threshold: '기본값 5',
};

// 드롭다운 각 옵션에 헤더+샘플 값을 함께 표기(carry-note #2 — 매핑 확인 UI는 감지 열의 샘플 값을
// 보여줘야 한다). 별도 미리보기 테이블을 두지 않고 옵션 라벨 자체에 정보를 실어 컴팩트하게 유지.
function formatColumnLabel(header: string, sampleRows: string[][], colIdx: number): string {
  const headerLabel = header.trim() || `(빈 헤더 · ${colIdx + 1}열)`;
  const sample = sampleRows[0]?.[colIdx]?.trim();
  return sample ? `${headerLabel} · 예: ${sample}` : headerLabel;
}

const NATIVE_SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring';

// ── 1. 파일 선택 ──────────────────────────────────────────────────

interface FileSelectStepProps {
  fileError: string | null;
  isAnalyzing: boolean;
  analyzingLabel: string | null;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onDownloadTemplate: () => void;
}

export function FileSelectStep({
  fileError,
  isAnalyzing,
  analyzingLabel,
  onFileChange,
  onDownloadTemplate,
}: FileSelectStepProps) {
  return (
    <div className="space-y-4 py-2">
      <p className="break-keep text-sm text-slate-600">
        상품명·판매가·현재고가 담긴 엑셀 파일(.xlsx, .xlsm)을 업로드하면 상품을 한 번에
        등록하거나 갱신할 수 있습니다.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="excel-import-file">엑셀 파일</Label>
        <input
          id="excel-import-file"
          type="file"
          accept=".xlsx,.xlsm"
          disabled={isAnalyzing}
          onChange={onFileChange}
          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800 disabled:opacity-50"
        />
      </div>
      {isAnalyzing && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {analyzingLabel ?? '파일을 분석하는 중...'}
        </div>
      )}
      {fileError && <p className="break-keep text-sm text-red-600">{fileError}</p>}
      <Button type="button" variant="outline" size="sm" onClick={onDownloadTemplate}>
        빈 템플릿 다운로드
      </Button>
    </div>
  );
}

// ── 2. 매핑 확인 (항상 표시 — carry-note #2) ──────────────────────

interface MappingConfirmStepProps {
  headers: string[];
  sampleRows: string[][];
  mapping: InventoryFieldMapping;
  onFieldChange: (field: InventoryMappingField, colIdx: number | null) => void;
}

export function MappingConfirmStep({ headers, sampleRows, mapping, onFieldChange }: MappingConfirmStepProps) {
  // 컨테이너의 '다음' disabled 판정과 동일한 파생 계산을 같은 mapping prop으로 독립 재계산
  // (mirror state 아님 — 단일 진실은 hasDuplicateMapping, codexloop R1 Fix 2).
  const duplicateColumnsSelected = hasDuplicateMapping(mapping);
  return (
    <div className="space-y-4 py-2">
      <p className="break-keep text-xs text-slate-500">상품명은 필수입니다. 나머지는 비워두면 기본값으로 채워집니다.</p>
      {FIELD_ORDER.map((field) => {
        const hint = FIELD_DEFAULT_HINT[field];
        const value = mapping[field];
        return (
          <div key={field} className="space-y-1.5">
            <Label htmlFor={`excel-import-map-${field}`}>
              {FIELD_LABELS[field]}
              {field === 'name' && <span className="ml-0.5 text-red-500">*</span>}
            </Label>
            <select
              id={`excel-import-map-${field}`}
              value={value === null ? '' : String(value)}
              onChange={(e) => onFieldChange(field, e.target.value === '' ? null : Number(e.target.value))}
              className={NATIVE_SELECT_CLASS}
            >
              <option value="">{hint ? `매핑 안 함 (${hint})` : '선택해 주세요'}</option>
              {headers.map((header, idx) => (
                <option key={idx} value={idx}>
                  {formatColumnLabel(header, sampleRows, idx)}
                </option>
              ))}
            </select>
          </div>
        );
      })}
      {duplicateColumnsSelected && (
        <p className="break-keep text-sm font-medium text-red-600">
          같은 열을 여러 필드에 선택할 수 없습니다. 필드마다 서로 다른 열을 선택해 주세요.
        </p>
      )}
    </div>
  );
}

// ── 3. 미리보기 ──────────────────────────────────────────────────

function ErrorRowList({ errors }: { errors: InventoryExcelRowError[] }) {
  const visible = errors.slice(0, 20);
  return (
    <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs text-red-700">
      {visible.map((e) => (
        <li key={e.rowNo}>
          {e.rowNo}행: {e.reason}
        </li>
      ))}
      {errors.length > visible.length && <li className="text-red-500">외 {errors.length - visible.length}건</li>}
    </ul>
  );
}

function DuplicateList({ duplicates }: { duplicates: InventoryExcelDuplicateGroup[] }) {
  const visible = duplicates.slice(0, 20);
  return (
    <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs text-red-700">
      {visible.map((d) => (
        <li key={d.norm}>
          '{d.norm}': {d.rowNos.join(', ')}행
        </li>
      ))}
      {duplicates.length > visible.length && (
        <li className="text-red-500">외 {duplicates.length - visible.length}건</li>
      )}
    </ul>
  );
}

interface PreviewStepProps {
  result: InventoryExcelParseResult;
  blockReason: string | null;
}

export function PreviewStep({ result, blockReason }: PreviewStepProps) {
  return (
    <div className="space-y-4 py-2">
      <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
        <p>
          유효한 행 <span className="font-semibold tabular-nums">{result.rows.length.toLocaleString('ko-KR')}</span>개
          {result.errors.length > 0 && (
            <>
              {' '}
              · 오류{' '}
              <span className="font-semibold tabular-nums text-red-600">
                {result.errors.length.toLocaleString('ko-KR')}
              </span>
              개
            </>
          )}
        </p>
        <p className="mt-0.5 text-xs text-slate-400">전체 데이터 행 {result.totalDataRows.toLocaleString('ko-KR')}개 중</p>
      </div>

      {result.errors.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-600">오류 행 (실행에서 제외)</p>
          <ErrorRowList errors={result.errors} />
        </div>
      )}

      {result.duplicates.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-red-600">파일 안 중복 상품명</p>
          <DuplicateList duplicates={result.duplicates} />
        </div>
      )}

      {blockReason && <p className="break-keep text-sm font-medium text-red-600">{blockReason}</p>}
    </div>
  );
}

// ── 4. 모드 선택 ──────────────────────────────────────────────────

const TOGGLE_ITEM_CLASS =
  'text-xs px-3 h-8 rounded-md bg-transparent text-gray-700 hover:bg-white/60 data-[state=on]:bg-white data-[state=on]:text-indigo-700';

interface ModeSelectStepProps {
  mode: BulkUpsertMode;
  onModeChange: (mode: BulkUpsertMode) => void;
  validRowCount: number;
  replaceDeletionCount: number;
  // 오류 행이 있는 파일에서 '파일 기준으로 교체'를 막는 사유(없으면 null = 교체 가능).
  replaceBlockReason: string | null;
}

export function ModeSelectStep({
  mode,
  onModeChange,
  validRowCount,
  replaceDeletionCount,
  replaceBlockReason,
}: ModeSelectStepProps) {
  const replaceBlocked = replaceBlockReason !== null;
  return (
    <div className="space-y-4 py-2">
      <div className="break-keep rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
        실행하면 <span className="font-semibold tabular-nums">{validRowCount.toLocaleString('ko-KR')}</span>개 상품이
        등록/갱신됩니다.
      </div>
      <div className="space-y-1.5">
        <Label>등록 방식</Label>
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(value) => {
            if (value === 'upsert' || value === 'replace') onModeChange(value);
          }}
          className="justify-start gap-1 rounded-lg bg-gray-200 p-1"
        >
          <ToggleGroupItem value="upsert" aria-label="추가/갱신만" className={TOGGLE_ITEM_CLASS}>
            추가/갱신만
          </ToggleGroupItem>
          <ToggleGroupItem
            value="replace"
            aria-label="파일 기준으로 교체"
            disabled={replaceBlocked}
            className={cn(TOGGLE_ITEM_CLASS, replaceBlocked && 'opacity-50')}
          >
            파일 기준으로 교체
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
      {replaceBlocked && <p className="break-keep text-sm font-medium text-red-600">{replaceBlockReason}</p>}
      {!replaceBlocked && mode === 'replace' && (
        <p className="break-keep text-sm font-medium text-amber-700">
          파일에 없는 상품은 삭제됩니다. 같은 이름으로 다시 등록하면 복원됩니다.
          {replaceDeletionCount > 0 && ` 이번에 삭제될 상품은 ${replaceDeletionCount.toLocaleString('ko-KR')}개입니다.`}
        </p>
      )}
    </div>
  );
}

// ── 5. 결과 요약 (RPC 카운트 5키 그대로 렌더 — carry-note #6) ──────

interface ResultStepProps {
  result: BulkUpsertResult;
}

export function ResultStep({ result }: ResultStepProps) {
  const rows: Array<{ label: string; value: number }> = [
    { label: '생성', value: result.created },
    { label: '갱신', value: result.updated },
    { label: '복원', value: result.restored },
    { label: '삭제', value: result.softDeleted },
    { label: '재고 조정', value: result.stockAdjusted },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 py-2 sm:grid-cols-3">
      {rows.map((row) => (
        <div key={row.label} className="rounded-lg bg-slate-50 px-3 py-2.5 text-center">
          <p className="text-xs text-slate-500">{row.label}</p>
          <p className="text-lg font-semibold tabular-nums text-slate-900">{row.value.toLocaleString('ko-KR')}</p>
        </div>
      ))}
    </div>
  );
}
